import React, { useState, useCallback } from 'react';
import ChampionPoolManager from './ChampionPoolManager';

/**
 * APEX v8.4.1 — OnboardingModal
 * 3 étapes : Rôle → Champion Pool → Récap
 * Réutilise ChampionPoolManager (drag & drop, zones Main/Flex/Sidelined)
 */

const ROLES = [
  { id: 'TOP',     label: 'Top',     emoji: '🛡️', desc: 'Splitpush & duels' },
  { id: 'JUNGLE',  label: 'Jungle',  emoji: '🌿', desc: 'Objectives & ganks' },
  { id: 'MID',     label: 'Mid',     emoji: '⚡', desc: 'Roaming & carrying' },
  { id: 'ADC',     label: 'ADC',     emoji: '🎯', desc: 'Damage & positioning' },
  { id: 'SUPPORT', label: 'Support', emoji: '💫', desc: 'Vision & protection' },
];

const STEPS = ['Rôle', 'Champions', 'Récap'];

export default function OnboardingModal({ onComplete, games = [], champions = [], version }) {
  const [step, setStep]               = useState(0);
  const [primaryRole, setPrimaryRole] = useState(null);
  const [secondaryRole, setSecondaryRole] = useState(null);
  const [pool, setPool]               = useState([]);
  const [loading, setLoading]         = useState(false);

  const fakeSettings = { champion_pool: pool };

  const handlePoolUpdate = useCallback((newPool) => {
    setPool(newPool);
  }, []);

  async function handleComplete() {
    setLoading(true);
    await onComplete({
      primary_role: primaryRole,
      secondary_role: secondaryRole,
      champion_pool: pool,
      onboarding_completed: true,
    });
    setLoading(false);
  }

  const canNext = step === 0 ? !!primaryRole : true;

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl bg-zinc-950 border border-zinc-800 shadow-2xl flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-800 shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-black text-xl text-white">
                APEX <span className="text-cyan-400">Climber</span>
              </div>
              <div className="text-[10px] text-zinc-500 tracking-[0.3em] uppercase mt-0.5">
                Configuration initiale
              </div>
            </div>
            <div className="text-[10px] text-zinc-600 font-mono">{step + 1} / {STEPS.length}</div>
          </div>
          <div className="flex gap-2">
            {STEPS.map((s, i) => (
              <div key={i} className="flex-1">
                <div className={`h-0.5 transition-all ${i <= step ? 'bg-cyan-400' : 'bg-zinc-800'}`} />
                <div className={`text-[9px] uppercase tracking-widest mt-1 ${i === step ? 'text-cyan-400' : i < step ? 'text-zinc-500' : 'text-zinc-700'}`}>{s}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">

          {step === 0 && (
            <div className="space-y-8">
              <div>
                <div className="text-xs font-bold text-zinc-300 uppercase tracking-wider mb-4">
                  Quel est ton rôle principal ?
                </div>
                <div className="grid grid-cols-5 gap-3">
                  {ROLES.map(r => (
                    <button key={r.id}
                      onClick={() => { setPrimaryRole(r.id); if (secondaryRole === r.id) setSecondaryRole(null); }}
                      className={`flex flex-col items-center gap-2 p-4 border transition ${
                        primaryRole === r.id
                          ? 'border-cyan-400 bg-cyan-400/10 text-cyan-400'
                          : 'border-zinc-800 hover:border-zinc-600 text-zinc-400'
                      }`}
                    >
                      <span className="text-3xl">{r.emoji}</span>
                      <span className="text-xs font-black uppercase tracking-wider">{r.label}</span>
                      <span className="text-[9px] text-zinc-600 text-center leading-tight">{r.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs font-bold text-zinc-300 uppercase tracking-wider mb-1">
                  Rôle secondaire <span className="text-zinc-600 font-normal normal-case tracking-normal">— comme en file ranked (optionnel)</span>
                </div>
                <div className="text-[10px] text-zinc-600 mb-4">Les benchmarks de coaching s'adapteront à ton rôle principal.</div>
                <div className="grid grid-cols-5 gap-3">
                  {ROLES.filter(r => r.id !== primaryRole).map(r => (
                    <button key={r.id}
                      onClick={() => setSecondaryRole(secondaryRole === r.id ? null : r.id)}
                      className={`flex flex-col items-center gap-2 p-3 border transition ${
                        secondaryRole === r.id
                          ? 'border-violet-400 bg-violet-400/10 text-violet-400'
                          : 'border-zinc-800 hover:border-zinc-600 text-zinc-500'
                      }`}
                    >
                      <span className="text-2xl">{r.emoji}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider">{r.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-500 border border-zinc-800 bg-zinc-900/30 p-3">
                Glisse les champions entre les zones. Tu pourras modifier ca a tout moment depuis l'onglet Champions.
              </div>
              <ChampionPoolManager
                games={games}
                userSettings={fakeSettings}
                champions={champions}
                version={version}
                onUpdatePool={handlePoolUpdate}
              />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="text-sm text-zinc-400">Tout est pret. Voici ta configuration :</div>

              <div className="border border-zinc-800 bg-zinc-900/30 p-4 space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Roles</div>
                <div className="flex gap-6">
                  {primaryRole && (
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{ROLES.find(r => r.id === primaryRole)?.emoji}</span>
                      <div>
                        <div className="text-xs font-black text-cyan-400">{ROLES.find(r => r.id === primaryRole)?.label}</div>
                        <div className="text-[9px] text-zinc-600 uppercase tracking-wider">Principal</div>
                      </div>
                    </div>
                  )}
                  {secondaryRole && (
                    <>
                      <div className="text-zinc-700 self-center text-lg">+</div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{ROLES.find(r => r.id === secondaryRole)?.emoji}</span>
                        <div>
                          <div className="text-xs font-black text-violet-400">{ROLES.find(r => r.id === secondaryRole)?.label}</div>
                          <div className="text-[9px] text-zinc-600 uppercase tracking-wider">Secondaire</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="border border-zinc-800 bg-zinc-900/30 p-4 space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                  Champion pool — {pool.length} champion{pool.length > 1 ? 's' : ''}
                </div>
                {pool.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {pool.map(c => {
                      const colorMap = { main: 'text-cyan-400 border-cyan-400/50 bg-cyan-400/10', flex: 'text-violet-400 border-violet-400/50 bg-violet-400/10', sidelined: 'text-zinc-500 border-zinc-700 bg-zinc-800/50' };
                      const cls = colorMap[c.status] || 'text-zinc-400 border-zinc-700';
                      return (
                        <span key={c.name} className={`text-[10px] font-bold px-2 py-0.5 border ${cls}`}>
                          {c.name}
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-xs text-zinc-600">Aucun champion — tu pourras configurer ca plus tard.</div>
                )}
              </div>

              <div className="text-xs text-zinc-500 border border-zinc-800 bg-zinc-900/20 p-3">
                Tu pourras modifier ton role et ton champion pool depuis les parametres.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-800 flex gap-3 shrink-0">
          {step > 0 && (
            <button onClick={() => setStep(s => s - 1)}
              className="border border-zinc-700 text-zinc-400 hover:text-zinc-200 text-sm px-5 py-2.5 transition font-semibold">
              Retour
            </button>
          )}
          <button
            onClick={() => step < STEPS.length - 1 ? setStep(s => s + 1) : handleComplete()}
            disabled={!canNext || loading}
            className={`flex-1 text-sm py-2.5 font-black tracking-wider transition ${
              canNext && !loading
                ? step === STEPS.length - 1
                  ? 'bg-cyan-400 hover:bg-cyan-300 text-black'
                  : 'bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700'
                : 'bg-zinc-900 text-zinc-600 cursor-not-allowed border border-zinc-800'
            }`}
          >
            {loading ? 'SAUVEGARDE...' : step === STEPS.length - 1 ? "C'EST PARTI" : 'CONTINUER'}
          </button>
        </div>
      </div>
    </div>
  );
}
